import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
  useAnswer,
  useCompleteLesson,
  useLanguage,
  useLoadLesson,
} from '../api/hooks'
import type { AnswerResult, Exercise, LessonCompleteResult } from '../api/types'
import { ExerciseView, type AnswerPayload } from '../components/exercises'
import { Badge, Button, Card, ErrorText, Spinner } from '../components/ui'
import { sfx } from '../lib/sound'
import { speak } from '../lib/speech'

/** How far from the end a missed exercise is spliced back in. */
const REQUEUE_OFFSET = 4

type Phase =
  | { kind: 'asking' }
  | { kind: 'feedback'; result: AnswerResult; requeued: boolean }

export default function Lesson() {
  const languageId = Number(useParams().id)
  const unitIndex = Number(useParams().unit)
  const navigate = useNavigate()

  const { data: language } = useLanguage(languageId)
  const loadLesson = useLoadLesson(languageId)
  const answer = useAnswer()
  const complete = useCompleteLesson(languageId)

  const [queue, setQueue] = useState<Exercise[]>([])
  const [position, setPosition] = useState(0)
  const [phase, setPhase] = useState<Phase>({ kind: 'asking' })
  const [correct, setCorrect] = useState(0)
  const [attempts, setAttempts] = useState(0)
  const [combo, setCombo] = useState(0)
  const [bestCombo, setBestCombo] = useState(0)
  const [xp, setXp] = useState(0)
  const [summary, setSummary] = useState<LessonCompleteResult | null>(null)

  const continueRef = useRef<HTMLButtonElement>(null)
  const load = loadLesson.mutate
  const totalPlanned = useRef(0)

  // Load once per unit. `load` is stable, so this does not re-fire on rerender.
  useEffect(() => {
    load(unitIndex, {
      onSuccess: (lesson) => {
        setQueue(lesson.exercises)
        totalPlanned.current = lesson.exercises.length
      },
    })
  }, [load, unitIndex])

  const current = queue[position]

  useEffect(() => {
    if (phase.kind === 'feedback') continueRef.current?.focus()
  }, [phase])

  const finish = useCallback(() => {
    sfx.complete()
    complete.mutate(
      { unitIndex, correct, total: attempts },
      {
        onSuccess: (result) => {
          setSummary(result)
          if (result.crown_earned) sfx.crown()
        },
      },
    )
  }, [complete, unitIndex, correct, attempts])

  function submit(payload: AnswerPayload) {
    if (!current || phase.kind !== 'asking') return

    answer.mutate(
      {
        entry_id: current.entry_id,
        direction: current.direction,
        ...payload,
      },
      {
        onSuccess: (result) => {
          setAttempts((n) => n + 1)

          if (result.correct) {
            const streak = combo + 1
            setCombo(streak)
            setBestCombo((b) => Math.max(b, streak))
            setCorrect((n) => n + 1)
            // The multiplier is what makes a run of right answers feel like
            // momentum rather than a list of ticks.
            setXp((x) => x + 10 * Math.min(streak, 5))
            if (streak >= 3) sfx.combo(streak)
            else sfx.correct()
            setPhase({ kind: 'feedback', result, requeued: false })
          } else {
            setCombo(0)
            sfx.wrong()
            requeue()
            setPhase({ kind: 'feedback', result, requeued: true })
          }
        },
      },
    )
  }

  /**
   * Put a missed exercise back near the end of the queue. The lesson therefore
   * ends only once everything has been answered correctly at least once --
   * no lives, no restart, no dead end.
   */
  function requeue() {
    setQueue((q) => {
      const missed = q[position]
      if (!missed) return q
      const rest = [...q]
      const insertAt = Math.max(position + 1, rest.length - REQUEUE_OFFSET)
      rest.splice(insertAt, 0, missed)
      return rest
    })
  }

  function next() {
    if (position + 1 >= queue.length) {
      finish()
      return
    }
    setPosition((p) => p + 1)
    setPhase({ kind: 'asking' })
  }

  // --- states -------------------------------------------------------------

  if (loadLesson.isPending && queue.length === 0) {
    return <Spinner label="Building your lesson" />
  }
  if (loadLesson.error) {
    return (
      <div className="mx-auto max-w-md space-y-4 text-center">
        <ErrorText error={loadLesson.error} />
        <Link to={`/languages/${languageId}/learn`}>
          <Button variant="secondary">Back to the path</Button>
        </Link>
      </div>
    )
  }

  if (summary) {
    return (
      <Summary
        summary={summary}
        correct={correct}
        attempts={attempts}
        xp={xp}
        bestCombo={bestCombo}
        languageId={languageId}
        unitIndex={unitIndex}
        onAgain={() => navigate(0)}
      />
    )
  }

  if (!current) return <Spinner />

  const progress = (position / Math.max(queue.length, 1)) * 100

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <div>
        <div className="mb-2 flex items-center justify-between text-sm">
          <Link
            to={`/languages/${languageId}/learn`}
            className="text-muted hover:text-ink"
          >
            ✕ Quit
          </Link>
          <div className="flex items-center gap-3">
            {combo >= 3 && (
              <span className="font-semibold text-brand">🔥 {combo}</span>
            )}
            <span className="tabular-nums text-muted">
              {position + 1} / {queue.length}
            </span>
          </div>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-border-subtle">
          <div
            className="h-full rounded-full bg-brand transition-[width] duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <Card className="space-y-5">
        <div className="flex items-center gap-2">
          <Badge tone="brand">{modeLabel(current, language?.name)}</Badge>
          {phase.kind === 'feedback' && phase.requeued && (
            <Badge>You'll see this again</Badge>
          )}
        </div>

        <ExerciseView
          exercise={current}
          locale={language?.pronunciation_locale ?? null}
          disabled={phase.kind === 'feedback' || answer.isPending}
          onAnswer={submit}
        />

        {phase.kind === 'feedback' && (
          <Feedback
            result={phase.result}
            exercise={current}
            locale={language?.pronunciation_locale ?? null}
            onNext={next}
            continueRef={continueRef}
          />
        )}

        <ErrorText error={answer.error} />
      </Card>
    </div>
  )
}

function modeLabel(exercise: Exercise, languageName?: string): string {
  switch (exercise.mode) {
    case 'multiple_choice':
      return 'Pick the right one'
    case 'scramble':
      return 'Build the word'
    case 'listen':
      return 'Listen'
    case 'true_false':
      return 'True or false'
    default:
      return exercise.direction === 'produce'
        ? `Write it in ${languageName ?? 'your language'}`
        : 'Translate it'
  }
}

function Feedback({
  result,
  exercise,
  locale,
  onNext,
  continueRef,
}: {
  result: AnswerResult
  exercise: Exercise
  locale: string | null
  onNext: () => void
  continueRef: React.RefObject<HTMLButtonElement | null>
}) {
  // Enter continues, so a lesson never needs the mouse.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        event.preventDefault()
        onNext()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onNext])

  const nearMiss = result.correct && result.grade < 5

  return (
    <div className="space-y-3">
      <div
        className={`rounded-lg px-4 py-3 text-center ${
          result.correct
            ? 'bg-positive/10 text-positive'
            : 'bg-negative/10 text-negative'
        }`}
      >
        <p className="font-medium">
          {result.correct ? (nearMiss ? 'Close enough' : 'Correct') : 'Not quite'}
        </p>
        {(!result.correct || nearMiss) && (
          <button
            onClick={() => speak(exercise.speak ?? result.expected, { locale })}
            className="mt-1 text-lg font-semibold text-ink hover:underline"
          >
            {result.expected}
          </button>
        )}
      </div>

      <Button ref={continueRef} onClick={onNext} className="w-full">
        Continue
      </Button>
    </div>
  )
}

function Summary({
  summary,
  correct,
  attempts,
  xp,
  bestCombo,
  languageId,
  unitIndex,
  onAgain,
}: {
  summary: LessonCompleteResult
  correct: number
  attempts: number
  xp: number
  bestCombo: number
  languageId: number
  unitIndex: number
  onAgain: () => void
}) {
  const accuracy = attempts > 0 ? Math.round((correct / attempts) * 100) : 100

  return (
    <div className="mx-auto max-w-md space-y-5 text-center">
      <div className="text-6xl">{summary.crown_earned ? '👑' : '✅'}</div>
      <h1 className="text-2xl font-semibold tracking-tight">
        {summary.crown_earned ? 'Crown earned' : 'Lesson complete'}
      </h1>

      <div className="grid grid-cols-3 gap-3">
        <Card className="py-4">
          <p className="text-xs uppercase tracking-wide text-muted">Accuracy</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{accuracy}%</p>
        </Card>
        <Card className="py-4">
          <p className="text-xs uppercase tracking-wide text-muted">XP</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{xp}</p>
        </Card>
        <Card className="py-4">
          <p className="text-xs uppercase tracking-wide text-muted">Best streak</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{bestCombo}</p>
        </Card>
      </div>

      <Card>
        <p className="text-sm text-muted">
          <span className="font-semibold text-brand">+{summary.tokens_awarded}</span>{' '}
          tokens · {summary.token_balance} in your wallet
        </p>
        {summary.unlocked_next && (
          <p className="mt-2 text-sm text-positive">Next unit unlocked</p>
        )}
        <div className="mt-2 flex justify-center gap-1">
          {Array.from({ length: 5 }, (_, i) => (
            <span key={i} className={i < summary.crowns ? '' : 'opacity-20'}>
              👑
            </span>
          ))}
        </div>
      </Card>

      <div className="flex justify-center gap-2">
        <Link to={`/languages/${languageId}/learn`}>
          <Button>Back to the path</Button>
        </Link>
        <Button variant="secondary" onClick={onAgain}>
          Practise unit {unitIndex + 1} again
        </Button>
      </div>
    </div>
  )
}
